import React, { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabaseClient';

export default function TabSocial({ topic, contentId, content, includeText }) {
  const [selectedPlatforms, setSelectedPlatforms] = useState({
    linkedin: false,
    x: false,
    instagram: false,
    facebook: false,
  });

  const [output, setOutput] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const platforms = [
    { key: 'linkedin', label: 'LinkedIn' },
    { key: 'x', label: 'X' },
    { key: 'instagram', label: 'Instagram' },
    { key: 'facebook', label: 'Facebook' },
  ];

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleCheckboxChange = (platform) => {
    setSelectedPlatforms((prev) => ({
      ...prev,
      [platform]: !prev[platform],
    }));
  };

  useEffect(() => {
    const fetchSavedPosts = async () => {
      if (!contentId) return;

      const { data, error } = await supabase
        .from('generated_social')
        .select('platform, text')
        .eq('content_id', contentId);

      if (error) {
        console.error('Failed to fetch saved social posts:', error);
        return;
      }

      const loadedOutput = {};
      data.forEach(({ platform, text }) => {
        loadedOutput[platform.toLowerCase()] = text;
      });

      setOutput(loadedOutput);
    };

    fetchSavedPosts();
  }, [contentId]);

  const handleGenerate = async () => {
    const activePlatforms = Object.keys(selectedPlatforms).filter(
      (platform) => selectedPlatforms[platform]
    );

    if (!activePlatforms.length) {
      alert('Please select at least one platform.');
      return;
    }

    if (!topic) {
      alert('Topic is required.');
      return;
    }

    if (!contentId) {
      alert('Content ID is missing. Cannot save social posts.');
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const res = await fetch('/api/generate-social', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content,
          includeText,
          platforms: activePlatforms,
        }),
      });

      const data = await res.json();

      if (!data.success) {
        alert(data.error || 'Failed to generate posts.');
        return;
      }

      setOutput((prev) => ({ ...prev, ...data.results }));

      const { data: userData } = await supabase.auth.getUser();
      const user = userData?.user;
      if (!user) {
        alert('User not logged in.');
        return;
      }

      await supabase
        .from('generated_social')
        .delete()
        .eq('content_id', contentId);

      await Promise.all(
        Object.entries(data.results).map(async ([platform, text]) => {
          const { error } = await supabase.from('generated_social').insert([
            {
              user_id: user.id,
              content_id: contentId,
              platform,
              text,
            },
          ]);

          if (error) {
            console.error(`Failed to save ${platform} post:`, error);
            setErrors((prev) => ({
              ...prev,
              [platform]: 'Failed to save this post.',
            }));
          }
        })
      );
    } catch (err) {
      console.error(err);
      alert('Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-output view-content pb-100-d">
      <h2>Generate Social Posts</h2>

      <div className="row mb-25">
        {platforms.map(({ key, label }) => (
          <label key={key}>
            <input
              type="checkbox"
              checked={selectedPlatforms[key]}
              onChange={() => handleCheckboxChange(key)}
            />
            {label}
          </label>
        ))}
      </div>

      <div className="content-actions justify-left">
        <button
          type="button"
          className="btn cici-think mt-0"
          onClick={handleGenerate}
          disabled={loading || !topic || !contentId}
        >
          {loading ? 'Generating…' : 'Generate Content'}
        </button>
      </div>

      <h2>Social Posts Output</h2>

      {platforms.map(({ key, label }) => (
        <div key={key} className="output-section">
          <label>{label}:</label>
          <div className="output-value textarea-shorter">
            <textarea readOnly value={output[key] || ''} />
            <button
              className="btn-unset"
              type="button"
              onClick={() => copyToClipboard(output[key])}
              disabled={!output[key]}
            >
              <i className="bi bi-copy" />
            </button>
          </div>
          {errors[key] && (
            <p style={{ color: 'red', fontSize: '0.9em' }}>{errors[key]}</p>
          )}
        </div>
      ))}
    </div>
  );
}